import { motion } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";

const roadPath = "M 120 430 C 220 380, 300 300, 390 250 C 470 205, 560 160, 690 95";

const wedge = (tx, ty, rx, ry, wT = 9, wR = 24) => {
  const dx = rx - tx;
  const dy = ry - ty;
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len;
  const py = dx / len;
  return `M ${tx + px * wT} ${ty + py * wT} L ${rx + px * wR} ${ry + py * wR} L ${rx - px * wR} ${ry - py * wR} L ${tx - px * wT} ${ty - py * wT} Z`;
};

const SOUND_ZONES = [
  wedge(108, 448, 138, 420),
  wedge(225, 335, 254, 358),
  wedge(235, 278, 302, 306),
  wedge(350, 135, 394, 244),
  wedge(430, 290, 430, 232),
  wedge(600, 375, 580, 268),
  wedge(640, 130, 656, 112),
];

const TOWNS = [
  { x: 108, y: 448, label: "Naarden", anchor: "start" },
  { x: 225, y: 335, label: "Crailo", anchor: "end", small: true },
  { x: 430, y: 290, label: "Bussum", anchor: "start" },
  { x: 600, y: 375, label: "Hilversum", anchor: "start" },
  { x: 235, y: 278, label: "Laren", anchor: "start" },
  { x: 350, y: 135, label: "Blaricum", anchor: "start" },
  { x: 640, y: 130, label: "Knooppunt Eemnes", anchor: "end", dy: -24 },
];

const AREAS = [
  { cx: 150, cy: 350, rx: 78, ry: 42, label: "woonwijken" },
  { cx: 330, cy: 330, rx: 66, ry: 38, label: "woonwijken" },
  { cx: 470, cy: 180, rx: 72, ry: 40, label: "woonwijken" },
  { cx: 600, cy: 230, rx: 90, ry: 52, label: "groen & natuur" },
  { cx: 220, cy: 180, rx: 100, ry: 55, label: "groen & natuur" },
];

export const TrajectoryMap = () => (
  <section id="traject" data-testid="map-section" className="scroll-mt-24 py-24 md:py-32">
    <div className="mx-auto max-w-[90rem] px-5 md:px-12">
      <Reveal>
        <Overline>Het traject</Overline>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-medium leading-tight tracking-tight text-gooi-green md:text-5xl">
          A1 Naarden/Bussum → knooppunt Eemnes
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-gooi-green/75 md:text-lg">
          Het gaat om circa 7 kilometer A1, in beide rijrichtingen. Direct naast dit traject liggen woonwijken,
          tuinen en het groen van het Gooi. Daarom maakt juist híér snelheid verschil.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          data-testid="trajectory-map"
          className="mt-12 overflow-hidden rounded-2xl border border-gooi-green/15 bg-gooi-sand"
        >
          <svg viewBox="0 0 800 500" role="img" aria-label="Schematische kaart van de A1 tussen Naarden/Bussum en knooppunt Eemnes met nabijgelegen woongebieden" className="h-auto w-full">
            {AREAS.map((a) => (
              <g key={a.label + a.cx}>
                <ellipse cx={a.cx} cy={a.cy} rx={a.rx} ry={a.ry} fill="#2A5C43" opacity="0.14" />
                <text x={a.cx} y={a.cy + 4} textAnchor="middle" fontSize="13" fill="#2A5C43" opacity="0.75" fontFamily="Manrope, sans-serif" fontStyle="italic">
                  {a.label}
                </text>
              </g>
            ))}
            <path d={roadPath} fill="none" stroke="#1A1B1A" strokeOpacity="0.12" strokeWidth="16" strokeLinecap="round" />
            {SOUND_ZONES.map((d, i) => (
              <motion.path
                key={i}
                d={d}
                fill="#D9383A"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.09 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.2, delay: 1.2 + i * 0.15 }}
              />
            ))}
            <motion.path
              d={roadPath}
              fill="none"
              stroke="#1A1B1A"
              strokeWidth="7"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 2.2, ease: [0.33, 1, 0.68, 1] }}
            />
            <motion.path
              d={roadPath}
              fill="none"
              stroke="#F4F3EF"
              strokeWidth="1.6"
              strokeDasharray="10 12"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 2.2, ease: [0.33, 1, 0.68, 1] }}
            />
            <g>
              <rect x="352" y="218" width="46" height="30" rx="6" fill="#D9383A" />
              <text x="375" y="239" textAnchor="middle" fontSize="16" fontWeight="700" fill="#FFFFFF" fontFamily="Outfit, sans-serif">
                A1
              </text>
            </g>
            {TOWNS.map((t) => (
              <g key={t.label}>
                <circle cx={t.x} cy={t.y} r={t.small ? 5.5 : 7} fill="#F4F3EF" stroke="#1C3B2B" strokeWidth="3" />
                <text
                  x={t.anchor === "end" ? t.x - 16 : t.x + 16}
                  y={t.y + (t.dy ?? 5)}
                  textAnchor={t.anchor}
                  fontSize={t.small ? 13 : 15}
                  fontWeight="600"
                  fill="#1C3B2B"
                  fontFamily="Manrope, sans-serif"
                >
                  {t.label}
                </text>
              </g>
            ))}
            <g fontFamily="Manrope, sans-serif" fontSize="12" fill="#1C3B2B" opacity="0.7">
              <line x1="40" y1="470" x2="76" y2="470" stroke="#1A1B1A" strokeWidth="6" strokeLinecap="round" />
              <text x="86" y="474">betreffend traject, ±7 km</text>
              <ellipse cx="310" cy="470" rx="12" ry="7" fill="#2A5C43" opacity="0.25" />
              <text x="330" y="474">woon- en groengebieden</text>
              <path d="M 540 476 L 556 462 L 556 478 Z" fill="#D9383A" opacity="0.3" />
              <text x="566" y="474">geluidszone (indicatief)</text>
            </g>
          </svg>
        </div>
        <p className="mt-4 text-xs text-gooi-green/55">
          Schematische weergave in campagnestijl — niet op schaal, geen officiële kaart.
        </p>
      </Reveal>
    </div>
  </section>
);
